import { createBasePlugin } from 'wix-rich-content-plugin-commons';
import { createLinkEntityData, hasLinksInSelection, getVisibleSelectionRect, fixPastedLinks, insertLinkInPosition } from 'wix-rich-content-editor-common';
import { addLinkPreview, LINK_PREVIEW_TYPE } from 'wix-rich-content-plugin-link-preview/libs/utils';
import { containsUrl } from 'wix-rich-content-common';
import React from 'react';
import { LINK_TYPE } from './types.js';
import LinkViewer from './LinkViewer.js';
import { linkEntityStrategy } from './strategy.js';
import createToolbar from './toolbar/createToolbar.js';
import { DEFAULTS } from './defaults.js';
import 'prop-types';
import 'classnames';
import 'wix-rich-content-common/dist/statics/schemas/plugin-link.schema.json';
import 'lodash';
import './link-viewer-68944646.js';
import './toolbar/inline-buttons.js';
import './toolbar/TextLinkButton.js';
import './toolbar/RemoveLinkButton.js';
import './icons/RemoveLinkIcon.js';
import './toolbar/UrlLinkButton.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __restKey = (key) => typeof key === "symbol" ? key : key + "";
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const createLinkPlugin = (config) => {
  const type = LINK_TYPE;
  const _a = config, { theme, anchorTarget, relValue, [type]: settings = {}, commonPubsub } = _a, rest = __objRest(_a, ["theme", "anchorTarget", "relValue", __restKey(type), "commonPubsub"]);
  const target = anchorTarget;
  const rel = relValue;
  settings.minLinkifyLength = settings.minLinkifyLength || 6;
  const toolbar = createToolbar(__spreadProps(__spreadValues({}, config), { settings, closeInlinePluginToolbar }));
  const decorators = [
    {
      strategy: linkEntityStrategy,
      component: (props) => {
        const componentData = props == null ? void 0 : props.contentState.getEntity(props == null ? void 0 : props.entityKey).getData();
        return /* @__PURE__ */ React.createElement(
          LinkViewer,
          __spreadValues({
            componentData,
            anchorTarget,
            relValue,
            theme
          }, props)
        );
      }
    }
  ];
  let linkifyData;
  const handleReturn = (event, editorState, pluginFunctions) => {
    linkifyData = getLinkifyData(editorState);
    if (shouldConvertToLinkPreview(linkifyData)) {
      const url = getBlockLinkUrl(linkifyData);
      const blockKey = linkifyData == null ? void 0 : linkifyData.block.getKey();
      if (url && blockKey) {
        const linkData = createLinkEntityData({
          url,
          target,
          rel
        });
        addLinkPreview(editorState, config, blockKey, linkData);
      }
    }
    return "not-handled";
  };
  const shouldConvertToLinkPreview = (linkifyData2) => {
    var _a2;
    return linkifyData2 && ((_a2 = linkifyData2.block) == null ? void 0 : _a2.getType()) === "unstyled" && config[LINK_PREVIEW_TYPE];
  };
  const getBlockLinkUrl = (linkifyData2) => {
    const { string, block } = linkifyData2;
    if (block.getText() === string) {
      return string;
    }
  };
  const handleBeforeInput = (chars, editorState, eventTimeStamp, pluginFunctions) => {
    if (/\s/.test(chars)) {
      linkifyData = getLinkifyData(editorState);
    }
    return "not-handled";
  };
  let prevContentState;
  const isPasteChange = (editorState) => {
    const contentState = editorState.getCurrentContent();
    const contentChanged = contentState !== prevContentState;
    prevContentState = contentState;
    return contentChanged && editorState.getLastChangeType() === "insert-fragment";
  };
  function openInlinePluginToolbar(commonPubsubData) {
    commonPubsub.set("cursorOnInlinePlugin", commonPubsubData);
  }
  function closeInlinePluginToolbar() {
    commonPubsub.set("cursorOnInlinePlugin", null);
  }
  const onChange = (editorState) => {
    const selection = editorState.getSelection();
    if (hasLinksInSelection(editorState) && selection.isCollapsed() && selection.getHasFocus()) {
      setTimeout(() => {
        const boundingRect = getVisibleSelectionRect(window);
        openInlinePluginToolbar({ type, boundingRect });
      });
    } else {
      closeInlinePluginToolbar();
    }
    let newEditorState = editorState;
    if (isPasteChange(editorState)) {
      newEditorState = fixPastedLinks(editorState, { anchorTarget, relValue });
    } else if (linkifyData) {
      newEditorState = addLinkAt(linkifyData, editorState);
    }
    linkifyData = void 0;
    return newEditorState;
  };
  const getLinkifyData = (editorState) => {
    const strData = findLastStringWithNoSpacesAndSoftLines(editorState);
    return shouldLinkify(strData) ? strData : void 0;
  };
  const shouldLinkify = (consecutiveString) => consecutiveString.string.length >= settings.minLinkifyLength && containsUrl(consecutiveString.string) && !(rangeContainsEntity(consecutiveString) && blockContainsPlainText(consecutiveString)) && !(settings == null ? void 0 : settings.disableAutoLink);
  const findLastStringWithNoSpacesAndSoftLines = (editorState) => {
    const selection = editorState.getSelection();
    const blockKey = selection.getAnchorKey();
    const block = editorState.getCurrentContent().getBlockForKey(blockKey);
    const text = block.getText();
    const endIndex = selection.getEndOffset();
    const spaceIndex = text.lastIndexOf(" ", endIndex) + 1;
    const softLineIndex = text.lastIndexOf("\n", endIndex) + 1;
    const index = Math.max(spaceIndex, softLineIndex);
    const string = text.slice(index, endIndex);
    return { string, block, blockKey, index, endIndex };
  };
  const rangeContainsEntity = ({ block, index, endIndex }) => {
    for (let i = index; i < endIndex; i++) {
      if (block.getEntityAt(i) !== null) {
        return true;
      }
    }
    return false;
  };
  const blockContainsPlainText = ({ block, string }) => block.getText().length > string.length;
  const addLinkAt = ({ string, index, endIndex, blockKey }, editorState) => {
    return insertLinkInPosition(editorState, blockKey, index, endIndex, {
      url: string,
      rel,
      target
    });
  };
  return createBasePlugin(
    __spreadValues({
      theme,
      toolbar,
      type,
      anchorTarget,
      relValue,
      settings,
      commonPubsub,
      defaultPluginData: DEFAULTS
    }, rest),
    { decorators, handleBeforeInput, handleReturn, onChange }
  );
};
createLinkPlugin.functionName = LINK_TYPE;

export { createLinkPlugin };
//# sourceMappingURL=createLinkPlugin.js.map
